import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { Environment } from '@r1-compass-library/environments/environment';
import { IamUser, IamUserSaveFormat, IamUserUpdateFormat } from '@r1-compass-library/models';
import { catchError, finalize, map, Observable, of, retry } from 'rxjs';
import { SpinnerService } from './spinner.service';

@Injectable({ providedIn: 'root' })
export class IamUserService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private BASE_URL: string = Environment.baseUrl + '/frontend/iam-users';

    get(id: IamUser['id']): Observable<IamUser> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamUserService.get';
        this.#spinnerService.addProcess(process, 'Getting IAM user...');
        return this.#http.get<IamUser>(`${this.BASE_URL}/${id}`, httpOptions).pipe(
            retry(0),
            catchError(() => {
                return of<IamUser>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getByUsername(username: IamUser['username']): Observable<IamUser> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams().set('username', username),
        };
        const process = 'IamUserService.getByUsername';
        this.#spinnerService.addProcess(process, 'Getting IAM user...');
        return this.#http.get<IamUser[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            map(res => res[0]),
            catchError(() => {
                return of<IamUser>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    create(iamUser: IamUserSaveFormat): Observable<IamUser> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamUserService.create';
        this.#spinnerService.addProcess(process, 'Creating IAM user...');
        return this.#http.post<IamUser>(`${this.BASE_URL}`, iamUser, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<IamUser>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    update(id: number, iamUser: IamUserUpdateFormat): Observable<IamUser> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...HttpHeaderConstants.mergePatch,
            }),
        };
        const process = 'IamUserService.update';
        this.#spinnerService.addProcess(process, 'Updating IAM user...');
        return this.#http.patch<IamUser>(`${this.BASE_URL}/${id}`, iamUser, httpOptions).pipe(
            catchError(error => {
                return of<IamUser>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    findAll(): Observable<IamUser[]> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams(),
        };
        const process = 'IamUserService.findAll';
        this.#spinnerService.addProcess(process, 'Getting IAM users...');
        return this.#http.get<IamUser[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<IamUser[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    findAllCompact(): Observable<IamUser[]> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams().set('compact', 'true'),
        };
        const process = 'IamUserService.findAllCompact';
        this.#spinnerService.addProcess(process, 'Getting IAM users...');
        return this.#http.get<IamUser[]>(`${this.BASE_URL}`, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<IamUser[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }
}
